// What Coach MARK reads about the trader: a compact summary of results the site has already
// worked out (Insights, Behavior, rules, accounts, Then vs now, milestones) plus the trading day
// being coached. Sent with each MARK request (mark.js on the server); MARK explains these
// numbers instead of calculating them.
import { accountStatus } from './balance';
import { detectBehavior, GROUPS, PATTERNS } from './behaviorDetect';
import { buildInsights } from './insights';
import { COMPARE_ROWS, compareStretches, milestones, MILESTONE_TEXT } from './journey';
import { dayKey, holdMs, netPnl, tradingDayKey } from './pnl';
import { firmOf } from './business';
import { choicesFor } from './compareSlices';
import { pointValue } from './tools/contracts';

const r2 = (n) => (typeof n === 'number' && Number.isFinite(n) ? Math.round(n * 100) / 100 : n);
const NY = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });

function overall(trades) {
  const nets = trades.map(netPnl);
  const wins = nets.filter((n) => n > 0);
  const losses = nets.filter((n) => n < 0);
  const days = new Set(trades.map((t) => tradingDayKey(t.date)));
  const sum = (l) => l.reduce((a, b) => a + b, 0);
  return {
    trades: trades.length, tradingDays: days.size, net: r2(sum(nets)), winRate: trades.length ? Math.round((wins.length / trades.length) * 100) : null,
    avgWin: wins.length ? r2(sum(wins) / wins.length) : null, avgLoss: losses.length ? r2(sum(losses) / losses.length) : null,
    profitFactor: losses.length ? r2(sum(wins) / -sum(losses)) : null,
  };
}

// How a trade moved while it was open, from 1-minute bars (the trade chart's data): the most it
// was up and down in dollars, and the share of its minutes spent in the red.
export function tradeMove(trade, bars) {
  if (!bars || !bars.length || !trade.exitDate) return null;
  const from = Math.floor(new Date(trade.date).getTime() / 1000 / 60) * 60;
  const to = new Date(trade.exitDate).getTime() / 1000;
  const inside = bars.filter((b) => b.t >= from && b.t <= to);
  if (!inside.length) return null;
  const dir = trade.side === 'Short' ? -1 : 1;
  const perPoint = (pointValue(trade.symbol) || 1) * Math.abs(trade.size || 1);
  const high = Math.max(...inside.map((b) => b.h));
  const low = Math.min(...inside.map((b) => b.l));
  // Points in the trade's favor at its best and worst moment.
  const best = dir > 0 ? high - trade.entryPrice : trade.entryPrice - low;
  const worst = dir > 0 ? low - trade.entryPrice : trade.entryPrice - high;
  const red = inside.filter((b) => (b.c - trade.entryPrice) * dir < 0).length;
  return {
    mostUp: Math.round(Math.max(0, best) * perPoint * 100) / 100,
    mostDown: Math.round(Math.min(0, worst) * perPoint * 100) / 100,
    minutesInRedPct: Math.round((red / inside.length) * 100),
  };
}

// Groups with at least 3 trades, best and worst by average P&L per trade.
function bestWorst(rows, n = 3) {
  const solid = rows.filter((r) => r.count >= 3).map((r) => ({ when: r.label, trades: r.count, net: r2(r.net), avg: r2(r.avg), winRate: Math.round(r.winRate) }));
  const sorted = [...solid].sort((a, b) => b.avg - a.avg);
  return { best: sorted.slice(0, n), worst: sorted.slice(-n).reverse().filter((r) => !sorted.slice(0, n).includes(r)) };
}

// The latest trading day with trades, or today.
export function latestTradingDay(trades) {
  const days = trades.map((t) => tradingDayKey(t.date)).sort();
  return days[days.length - 1] || tradingDayKey(new Date());
}

export function buildMarkSnapshot({ trades, notes = {}, accounts = [], rules = [], library = [], day, scope = 'All accounts', moves = {} }) {
  const coached = day || latestTradingDay(trades);
  const behavior = detectBehavior(trades, { accounts, notes });
  const insights = trades.length >= 5 ? buildInsights({ trades, notes, rules, library, accounts }) : null;
  const byAccount = new Map(accounts.map((a) => [String(a._id), a]));

  // The coached day, trade by trade.
  const todays = trades.filter((t) => tradingDayKey(t.date) === coached).sort((a, b) => new Date(a.date) - new Date(b.date));
  const note = notes[coached] || notes[dayKey(new Date(`${coached}T12:00:00`))] || {};
  // Named coachedDay, not today: it's the latest day with trades, which is often an earlier day.
  const coachedDay = {
    day: coached,
    isToday: coached === tradingDayKey(new Date()),
    ...overall(todays),
    trades: todays.map((t) => ({
      time: t.timeUnknown ? null : NY.format(new Date(t.date)), symbol: t.symbol, side: t.side, size: t.size, net: r2(netPnl(t)),
      heldMinutes: holdMs(t) === null ? null : Math.round(holdMs(t) / 60000), account: (byAccount.get(String(t.accountId)) || {}).name,
      tags: t.tags && t.tags.length ? t.tags : undefined, grade: t.grade, notes: t.notes ? String(t.notes).slice(0, 300) : undefined,
      flags: (behavior.flags.get(t._id) || []).map((f) => `${PATTERNS.find((p) => p.kind === f.kind).name}: ${f.text}`),
      whileOpen: moves[t._id] || undefined,
    })),
    journal: {
      plan: note.plan ? String(note.plan).slice(0, 800) : undefined, review: note.review ? String(note.review).slice(0, 800) : undefined,
      workOn: note.workOn || undefined, focus: note.focus || undefined, focusFollowed: note.focusFollowed,
      moodBefore: note.moodBefore && note.moodBefore.length ? note.moodBefore : undefined, moodAfter: note.moodAfter && note.moodAfter.length ? note.moodAfter : undefined,
      emotionsHit: note.emotionsHit && note.emotionsHit.length ? note.emotionsHit : undefined, emotionTrigger: note.emotionTrigger || undefined,
      rulesFollowed: rules.length ? `${(note.rulesFollowed || []).length} of ${rules.length}` : undefined,
      rulesMissed: rules.length && note.rulesFollowed ? rules.filter((r) => !note.rulesFollowed.includes(r)) : undefined,
      sessionNotes: (note.sessionLog || []).slice(-15).map((e) => `${NY.format(new Date(e.time))} ${e.type}: ${String(e.text || '').slice(0, 200)}`),
      morningDone: (note.morningDone || []).length, eveningDone: (note.eveningDone || []).length,
    },
  };

  // The last 10 trading days, one line each.
  const dayKeys = [...new Set(trades.map((t) => tradingDayKey(t.date)))].sort().slice(-10);
  const recentDays = dayKeys.map((d) => {
    const list = trades.filter((t) => tradingDayKey(t.date) === d);
    const n = notes[d] || {};
    return { day: d, net: r2(list.reduce((s, t) => s + netPnl(t), 0)), trades: list.length, flagged: list.filter((t) => behavior.flags.has(t._id)).length,
      journaled: Boolean((n.eveningDone || []).length || n.review), emotions: n.emotionsHit && n.emotionsHit.length ? n.emotionsHit : undefined };
  });

  const stretch = compareStretches(trades, notes, accounts, 'start');
  const ms = milestones(accounts, trades, notes);

  // When the trader does well and badly: sessions (New York time), half hours (their local
  // time) and days of the week.
  const sessionRows = choicesFor('sessions', trades, {}).map((c) => {
    const list = trades.filter(c.test);
    const nets = list.map(netPnl);
    const net = nets.reduce((a, b) => a + b, 0);
    return { label: c.label, count: list.length, net, avg: list.length ? net / list.length : 0, winRate: list.length ? (nets.filter((x) => x > 0).length / list.length) * 100 : 0 };
  });
  const edge = insights ? {
    sessionsNewYorkTime: bestWorst(sessionRows, 2),
    halfHoursLocalTime: bestWorst(insights.timeRows),
    weekdays: bestWorst(insights.weekdayRows, 2),
  } : null;

  return {
    accountsInThisData: scope,
    summary: { allTime: overall(trades), since: trades.length ? dayKey([...trades].sort((a, b) => new Date(a.date) - new Date(b.date))[0].date) : null },
    todayIs: tradingDayKey(new Date()),
    coachedDay,
    recentDays,
    edge,
    behavior: {
      normal: behavior.baseline,
      flaggedCost: r2(behavior.flagged.net), flaggedTrades: behavior.flagged.count, otherTradesAvg: r2(behavior.clean.avg),
      groups: behavior.groups.map((g) => ({
        name: GROUPS.find((x) => x.id === g.id).name, signal: g.signal, detectedNet: r2(g.detected.net), detectedTrades: g.detected.count,
        reportedDays: g.reported.days, sameDays: g.overlap,
        patterns: g.patterns.filter((p) => p.count).map((p) => ({ name: p.name, trades: p.count, days: p.days, net: r2(p.net), winRate: Math.round(p.winRate) })),
      })).filter((g) => g.detectedTrades || g.reportedDays || g.patterns.length),
      worstDays: behavior.worstDays.slice(0, 3).map((d) => ({ day: d.day, flaggedNet: r2(d.flaggedNet), dayNet: r2(d.dayNet), patterns: d.kinds })),
    },
    insights: insights ? {
      honest: insights.honest.slice(0, 4).map((f) => `${f.area}: ${f.text}${f.small ? ' (early observation)' : ''}`),
      working: insights.working.map((f) => `${f.area}: ${f.text}`),
      costing: insights.costing.map((f) => `${f.area}: ${f.text}`),
    } : null,
    rules,
    accounts: accounts.filter((a) => !a.archived && !a.attemptOf).map((a) => {
      const s = accountStatus(a, trades.filter((t) => String(t.accountId) === String(a._id)));
      return {
        name: a.name, type: a.type, firm: firmOf(a) || undefined, size: a.size,
        ...(s ? {
          balance: r2(s.balance), change: r2(s.change), toMaxLoss: r2(s.toBlow), toTarget: r2(s.toPass), dailyLeft: r2(s.dailyLeft), risk: s.overall,
          consistency: s.consistencyPct != null ? `${Math.round(s.consistencyPct)}% (limit ${s.consistencyLimit}%)` : undefined,
          payout: s.payoutNeeded ? `${s.payoutDays} of ${s.payoutNeeded} winning days` : undefined,
        } : {}),
      };
    }),
    progress: stretch && stretch.then && stretch.now ? Object.fromEntries(COMPARE_ROWS.map(([k, label]) => [label, { first30Days: r2(stretch.then[k]), last30Days: r2(stretch.now[k]) }])) : null,
    milestones: { earned: ms.filter((m) => m.earnedOn).map((m) => MILESTONE_TEXT[m.id][1]), next: ms.filter((m) => !m.earnedOn).slice(0, 4).map((m) => `${MILESTONE_TEXT[m.id][1]} (${m.have}/${m.need})`) },
  };
}
